import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Control,
  useForm,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

import { ALL_FALSE_POSITIVE, EReputationStatus } from "src/core/constants/eReputation";
import { useFolder } from "src/core/contexts/FolderContext";
import {
  useCountriesListQuery,
  usePersonEReputationDjDetailsQuery,
  useUpdateEntityEReputationMutation,
} from "src/core/gql/graphql";
import { toast } from "src/core/hooks/use-toast";
import { EReputationMatchResult, MappedEntity } from "src/core/types/entity";
import { parseRawMatchResultsXml } from "src/core/utils/dowJonesV1";
import {
  parseRawMatchResultsEntityJson,
  parseRawMatchResultsJson,
} from "src/core/utils/dowJonesV2";
import { buildUpdateEntityEreputationInputs } from "src/core/utils/eReputation";

type UseEntityDetailsProps = {
  entity?: MappedEntity;
  open: boolean;
  setOpen: (open: boolean) => void;
};

type UseEntityDetailsReturn = {
  isPep: boolean;
  isSanction: boolean;
  isLoading: boolean;
  eReputationMatchResults: EReputationMatchResult[];
  selectedMatch: EReputationMatchResult | null;
  validationError: string | null;
  setValidationError: (error: string | null) => void;
  updatingEReputation: boolean;
  onSubmit: (values: Record<string, boolean | undefined>) => void;
  control: Control<Record<string, boolean | undefined>>;
  reset: () => void;
  handleSubmit: UseFormHandleSubmit<Record<string, boolean | undefined>>;
  setValue: UseFormSetValue<Record<string, boolean | undefined>>;
  watch: UseFormWatch<Record<string, boolean | undefined>>;
  handleSelectMatch: (match: EReputationMatchResult) => void;
  isReadOnly?: boolean;
  isPPH: boolean;
};

type MatchFormValues = Record<string, boolean | undefined>;

/**
 * Hook pour récupérer les détails (e-reputation) d'une entité (personne ou entreprise).
 */
export const useEntityDetails = ({
  entity,
  open,
  setOpen,
}: UseEntityDetailsProps): UseEntityDetailsReturn => {
  const { refetch, isReadOnly, isPPH } = useFolder();

  const [selectedMatch, setSelectedMatch] = useState<EReputationMatchResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [updatingEReputation, setUpdatingEReputation] = useState(false);

  const { data: countriesList, loading: countriesListLoading } = useCountriesListQuery();

  const [updateEntityEreputation] = useUpdateEntityEReputationMutation();

  const isPep =
    entity?.pepStatus === EReputationStatus.CONFIRMED ||
    entity?.pepStatus === EReputationStatus.FALSE_POSITIVE ||
    entity?.pepStatus === EReputationStatus.POTENTIAL;
  const isSanction =
    entity?.sanctionStatus === EReputationStatus.CONFIRMED ||
    entity?.sanctionStatus === EReputationStatus.FALSE_POSITIVE ||
    entity?.sanctionStatus === EReputationStatus.POTENTIAL;

  const { data, loading } = usePersonEReputationDjDetailsQuery({
    variables: {
      id: entity?.id ?? "",
    },
    skip: !entity || (!isPep && !isSanction),
  });

  /**
   * Récupère les résultats de l'e-reputation.
   * Si les résultats sont disponibles et que la liste des pays est disponible,
   * on parse les résultats.
   */
  const eReputationMatchResults: EReputationMatchResult[] = useMemo(() => {
    if (data?.entity?.eReputation?.details?.searchResults && countriesList) {
      const format = data.entity.eReputation.details?.format;
      const isEntityPPH = entity?.isPPH || (isPPH && !entity?.isPPH);
      if (format === "DJ_V2") {
        return isEntityPPH
          ? parseRawMatchResultsJson(
              data.entity.eReputation.details.searchResults,
              data.entity.eReputation.details.detailedResults
            )
          : parseRawMatchResultsEntityJson(
              data.entity.eReputation.details.searchResults,
              data.entity.eReputation.details.detailedResults
            );
      } else {
        return parseRawMatchResultsXml(
          data.entity.eReputation.details.searchResults,
          data.entity.eReputation.details.detailedResults,
          countriesList
        );
      }
    } else {
      return [];
    }
  }, [data, countriesList, entity?.isPPH, isPPH]);

  /**
   * Valide le formulaire de sélection des correspondances.
   * Si un champ est vide, on affiche un message d'erreur.
   * Si tous les champs sont remplis, on retourne true.
   */
  const validateForm = (values: Record<string, boolean | undefined>): boolean => {
    const valuesArray = Object.entries(values).filter(([key]) => key !== ALL_FALSE_POSITIVE);
    const hasUnselected = valuesArray.some(([, val]) => val === undefined);

    if (hasUnselected) {
      setValidationError("modal.entityDetails.form.error");
      return false;
    }

    setValidationError(null);
    return true;
  };

  /**
   * Gère la mise à jour des résultats de l'e-reputation.
   * Si le formulaire n'est pas valide, on ne fait rien.
   * Sinon, on met à jour les correspondances de l'entité.
   * On affiche un message de succès ou d'erreur.
   * On ferme la modal.
   */
  const onSubmit = useCallback(
    async (values: Record<string, boolean | undefined>) => {
      if (!entity || !validateForm(values)) {
        return;
      }

      setUpdatingEReputation(true);
      try {
        const inputs = buildUpdateEntityEreputationInputs({
          entity,
          values,
          eReputationMatchResults,
        });

        await updateEntityEreputation({
          variables: {
            entityId: entity.id,
            inputs: inputs,
          },
        });

        refetch();

        toast({
          description: t("modal.entityDetails.success"),
        });

        setOpen(false);
      } catch (error) {
        toast({
          description: t("modal.entityDetails.error"),
          variant: "destructive",
        });
        console.error("useEntitiesList::handleCallEReputation()", error);
      } finally {
        setUpdatingEReputation(false);
      }
    },
    [entity, refetch, updateEntityEreputation, setOpen, eReputationMatchResults]
  );

  const { control, reset, handleSubmit, setValue, watch } = useForm<MatchFormValues>();

  useEffect(() => {
    if (open && eReputationMatchResults.length > 0) {
      const defaultValues = eReputationMatchResults.reduce((acc, match) => {
        acc[match.id] = undefined;
        return acc;
      }, {} as MatchFormValues);
      reset(defaultValues);
      setValidationError(null);
    }
  }, [open, eReputationMatchResults, reset]);

  /**
   * Gère la sélection d'une correspondance.
   * Si aucune correspondance n'est sélectionnée, on sélectionne la première correspondance.
   * Si la correspondance sélectionnée n'est pas dans la liste des correspondances,
   * on sélectionne la première correspondance.
   */
  useEffect(() => {
    if (eReputationMatchResults.length > 0) {
      if (
        !selectedMatch ||
        !eReputationMatchResults.find((match) => match.id === selectedMatch.id)
      ) {
        setSelectedMatch(eReputationMatchResults[0]);
      }
    } else {
      setSelectedMatch(null);
    }
  }, [eReputationMatchResults, selectedMatch]);

  /**
   * Gère la sélection d'une correspondance.
   * On met à jour la correspondance sélectionnée.
   * "..." est utilisé pour s'assurer que la correspondance est une nouvelle instance de l'objet
   * et donc que le state est mis à jour.
   */
  const handleSelectMatch = (match: EReputationMatchResult) => {
    setSelectedMatch({ ...match });
  };

  return {
    isPep,
    isSanction,
    isLoading: countriesListLoading || loading,
    eReputationMatchResults,
    selectedMatch,
    validationError,
    setValidationError,
    updatingEReputation,
    onSubmit,
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    handleSelectMatch,
    isReadOnly,
    isPPH: entity?.isPPH || (isPPH && !entity?.isPPH),
  };
};
